import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, ArrowRight, X, Scroll, Wrench } from "@phosphor-icons/react";

/**
 * Debounced, multi-field global product search.
 * Calls /api/products?search=... which now matches across manufacturer + series + color + SKU.
 * Displays a dropdown of matches; click → navigates to /browse/product/:id.
 */
export default function GlobalSearch({ placeholder = "Search by brand, series, color or SKU…", autoFocus = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfrs, setMfrs] = useState({});
  const [series, setSeries] = useState({});
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Load lookups once for display
  useEffect(() => {
    Promise.all([api.get("/manufacturers"), api.get("/series")]).then(([m, s]) => {
      setMfrs(Object.fromEntries(m.data.map((x) => [x.id, x])));
      setSeries(Object.fromEntries(s.data.map((x) => [x.id, x])));
    });
  }, []);

  // Debounced search with AbortController to cancel stale in-flight requests
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    const controller = new AbortController();
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", {
          params: { search: q.trim() },
          signal: controller.signal,
        });
        setResults(data.slice(0, 12));
        setActiveIdx(-1);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          // genuine error — clear results
          setResults([]);
        }
      } finally { setLoading(false); }
    }, 250);
    return () => { clearTimeout(id); controller.abort(); };
  }, [q]);

  // Click outside closes dropdown
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onPick = (p) => {
    setOpen(false);
    setQ("");
    setResults([]);
    navigate(`/browse/product/${p.id}`);
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && results[activeIdx]) onPick(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && q.trim().length > 0;

  return (
    <div ref={wrapRef} className="relative w-full" data-testid="global-search">
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          autoFocus={autoFocus}
          placeholder={placeholder}
          className="h-11 pl-10 pr-10 rounded-md bg-white text-sm"
          data-testid="global-search-input"
        />
        {q && (
          <button onClick={() => { setQ(""); setResults([]); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
            data-testid="global-search-clear">
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-30 max-h-[420px] overflow-y-auto"
          data-testid="global-search-results">
          {loading && results.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500">No products match <span className="font-mono">{q}</span></div>
          ) : (
            <ul role="listbox">
              {results.map((p, i) => {
                const isCount = p.inventory_type === "count";
                return (
                  <li key={p.id}>
                    <button
                      onClick={() => onPick(p)}
                      onMouseEnter={() => setActiveIdx(i)}
                      className={`w-full text-left px-4 py-2.5 flex items-center gap-3 border-b last:border-0 border-slate-100 ${
                        activeIdx === i ? "bg-slate-100" : "hover:bg-slate-50"
                      }`}
                      data-testid={`global-search-result-${p.id}`}
                    >
                      {!isCount ? (
                        <div className="w-8 h-8 border border-slate-200 rounded-sm shrink-0" style={{ background: p.color_hex || "#e2e8f0" }} />
                      ) : (
                        <div className="w-8 h-8 grid place-items-center bg-amber-50 border border-amber-200 rounded-sm shrink-0">
                          <Wrench size={14} className="text-amber-700" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{p.name}</div>
                        <div className="text-[10px] font-mono tracking-[0.1em] uppercase text-slate-500 truncate">
                          {mfrs[p.manufacturer_id]?.name || "—"} · {series[p.series_id]?.name || "—"} · {p.base_sku}
                        </div>
                      </div>
                      <span className={`text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-sm border shrink-0 ${
                        isCount ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200"
                      }`}>
                        {isCount ? <Wrench size={9} className="inline mr-0.5" /> : <Scroll size={9} className="inline mr-0.5" />}
                        {isCount ? "Count" : "Rolls"}
                      </span>
                      <ArrowRight size={14} className="text-slate-400 shrink-0" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {results.length >= 12 && (
            <Link
              to={`/browse?q=${encodeURIComponent(q)}`}
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-xs font-mono tracking-[0.15em] uppercase text-blue-600 hover:bg-blue-50 border-t border-slate-200"
            >
              View all results in Browse →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
